// The Workspaces page as an inventory of the team's finding investigations:
// what each is, whose it is, when it was opened and when it was last worked on. Nothing
// here calls the network; components/WorkspaceInventory.jsx reads and draws.
import { INBOX_SCOPE } from "./caseInbox.js";
import { formatAgo } from "./escalationLabels.js";
import { caseState, passesWords, searchWords } from "./findingsConsole.js";
import { caseCanvasId } from "./traceCanvasStore.js";

const DAY = 86_400_000;
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const ON_DATE_YEAR = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
const lower = (v) => String(v ?? "").trim().toLowerCase();

// What each column sorts on. Dates sort newest first; the unassigned sort
// after every owner.
const SORT_KEY = {
  title: () => (inv) => lower(inv.title),
  owner: (nameOf) => (inv) => (inv.ownerUserId ? lower(nameOf(inv.ownerUserId)) : "￿"),
  opened: () => (inv) => -(inv.createdAtMs || 0),
  activity: () => (inv) => -(inv.updatedAtMs || 0),
};
export const SORTS = Object.keys(SORT_KEY);

// Where an investigation stands, as the status filter names it. The groups
// follow the Overview's cards (lib/overview.js investigationsIn): open with
// nobody holding it, held, or closed as a true positive, a false positive,
// or otherwise (joined, mixed, inconclusive, rejected).
export const STATUSES = ["unassigned", "investigating", "truePositive", "falsePositive", "otherClosed"];
export const STATUS_LABEL = { unassigned: "Unassigned", investigating: "Investigating", truePositive: "True positive", falsePositive: "False positive", otherClosed: "Other closed" };
export const OPEN_STATUSES = ["unassigned", "investigating"];

/**
 * The open investigations and the closed ones, read apart (by id), as one
 * map. One read in both, having closed or reopened between the two reads,
 * is taken as it was last worked on.
 */
export function mergeReads(open, closed) {
  const out = new Map(open);
  for (const [id, inv] of closed || []) {
    const had = out.get(id);
    if (!had || (inv.updatedAtMs || 0) >= (had.updatedAtMs || 0)) out.set(id, inv);
  }
  return out;
}

// How the reader left the Investigations page, found again on coming back
// from a board; the Overview's cards set it before opening the page. One date
// range (`range`, a range id, or null for any time) applies to the date
// `dateField` names. The search stays in memory: it is tenant content.
export const DATE_FIELDS = { opened: "Opened", activity: "Last activity" };
export const inventoryView = { statuses: OPEN_STATUSES, dateField: "opened", range: null, query: "", by: "activity", reverse: false };
export function setInventoryView(patch) {
  Object.assign(inventoryView, patch);
}

export function inventoryStatus(inv) {
  const s = caseState(inv);
  if (s.state === "new") return "unassigned";
  if (s.state === "investigating") return "investigating";
  if (s.state === "tp") return "truePositive";
  if (s.state === "joined" || s.reason === "mixed" || s.reason === "inconclusive" || s.reason === "other") return "otherClosed";
  return "falsePositive";
}

const inSpan = (ms, span) => !span || (Number.isFinite(ms) && ms >= span.from && ms <= span.to);
const searchText = (inv, nameOf) => lower([inv.title, inv.summary, inv.ownerUserId ? nameOf(inv.ownerUserId) : "", STATUS_LABEL[inventoryStatus(inv)]].filter(Boolean).join("\n"));
// Whether an investigation was opened in `opened` and last worked on in
// `activity` ({ from, to } ms, or null for any time), and has every word of `query`.
function matches(inv, { opened = null, activity = null, query = "", nameOf = (id) => id }) {
  if (!inSpan(inv.createdAtMs, opened) || !inSpan(inv.updatedAtMs, activity)) return false;
  const words = searchWords(query);
  return !words.length || passesWords(searchText(inv, nameOf), words);
}

/** How many investigations of each status the date ranges and search leave (see `matches`). */
export function statusCounts(byId, filter = {}) {
  const out = Object.fromEntries(STATUSES.map((s) => [s, 0]));
  for (const inv of byId?.values() || []) if (matches(inv, filter)) out[inventoryStatus(inv)] += 1;
  return out;
}

/**
 * The investigations listed: those of the `statuses` picked (none picked is
 * every status) that the date ranges and search leave (see `matches`), sorted
 * by a column (`by`, one of SORTS; `reverse` flips it). `nameOf(userId)` names
 * an owner, for the search and for sorting by owner.
 */
export function listed(byId, { statuses = OPEN_STATUSES, by = "activity", reverse = false, ...filter } = {}) {
  const nameOf = filter.nameOf || ((id) => id);
  const key = (SORT_KEY[by] || SORT_KEY.activity)(nameOf);
  const picked = new Set(statuses);
  const rows = [...(byId?.values() || [])].filter((inv) => (!picked.size || picked.has(inventoryStatus(inv))) && matches(inv, filter));
  rows.sort((a, b) => {
    const x = key(a);
    const y = key(b);
    return (x < y ? -1 : x > y ? 1 : 0) || String(a.incidentId).localeCompare(String(b.incidentId));
  });
  return reverse ? rows.reverse() : rows;
}

/** Where a closed investigation ended, in words; empty while it is open. */
export function statusWords(inv) {
  if (inv?.status !== "closed") return "";
  const s = caseState(inv);
  if (s.state === "tp") return "Closed as a true positive";
  if (s.state === "joined") return `Joined to ${s.joined.title}`;
  if (s.reason === "mixed") return "Closed with a mixed outcome";
  if (s.reason === "inconclusive") return "Closed as inconclusive";
  if (s.reason === "other") return "Rejected";
  return "Closed as a false positive";
}

/** A day, with its year when it is not this one. */
export function onDate(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  return (new Date(ms).getFullYear() === new Date(now).getFullYear() ? ON_DATE : ON_DATE_YEAR).format(ms);
}

/** When, in words: how long ago within two weeks, the date before. */
export function when(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return "";
  return now - ms < 14 * DAY ? formatAgo(new Date(ms), now) : onDate(ms, now);
}

/**
 * The board this browser keeps for an investigation, from its members
 * (`findings` of a Get): the board of the case a member was saved from, else
 * a case board holding one of its findings. `boards`: the boards kept here,
 * each { id, findings: [finding id] }. Null when there is none.
 */
export function boardOf(members, boards, investigationId = null) {
  if (investigationId) {
    const bound = boards.find((b) => b.investigationId === investigationId);
    if (bound) return bound.id;
    boards = boards.filter((b) => !b.investigationId);
  }
  const kept = new Set(boards.map((b) => b.id));
  for (const m of members || []) {
    const id = caseCanvasId(INBOX_SCOPE, m.source?.case);
    if (id && kept.has(id)) return id;
  }
  const ids = new Set((members || []).map((m) => lower(m.finding_id)).filter(Boolean));
  return boards.find((b) => b.id.startsWith("case") && b.findings.some((f) => ids.has(lower(f))))?.id || null;
}
